/* ============================================================================
   M4 — PAGE 2: Zone Override  (route /supply/zone-override)
   A4 modern UI. Account-code-level override layer above the courier-side base
   zones — scoped to COURIER ACCOUNT CODES (not merchants/clients); the codes
   are shaped like the prior client objects so the override engine is unchanged.
   The resolution engine (resolveZone / resolveClientZone / activeOverrides /
   buildCourierRows / dualRows / MATCH_LABEL) is ported untouched from js/m4.js;
   this presentation consumes the SAME dualRows()/activeOverrides() output.
   Rule-cards (default, with table-view toggle + auto-fallback beyond 15 rules) +
   a base→override transform preview with an All / Overridden-only segmented
   filter, a builder drawer, CSV-upload drawer, and deactivate-with-reason.
   ========================================================================== */
import React, {useMemo, useState} from 'react'
import {Autocomplete, TextField} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import SyncIcon from '@mui/icons-material/SyncOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import UploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import CheckIcon from '@mui/icons-material/CheckOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import PageTitle from '../../../components/PageTitle'
import {
  KpiStrip,
  SupplyTable,
  SideDrawer,
  ConfirmDialog,
  FileDrop,
  useToast,
  cx,
  download,
  fmtDate,
  fmtDateTime,
} from '../components'
import {DUMMY_CODES, DUMMY_AGREEMENTS, TODAY} from '../dummyData'
import {
  ZONE_NAMES,
  MATCH_FIELDS,
  MATCH_LABEL,
  pin,
  resolveZone,
  resolveClientZone,
  uniqueCities,
  uniqueStates,
  datesOverlap,
  METROS_SEED,
  SPECIALS_SEED,
} from './zoneEngine'
import {OVERRIDES, activeOverrides, nextOverrideId} from './overridesData'
import {ZoneBadge, BluePill, AgreementCell} from './zoneBits'
import {ZO_CSS} from './zoCss'

/* Zone Override is scoped to COURIER ACCOUNT CODES (not merchants/clients). The
   codes are shaped like the prior client objects, so the override engine is
   unchanged. */
const CODE_CLIENTS = DUMMY_CODES.map((c) => ({
  id: c.code,
  name: c.display || c.code,
  code: c.code,
  parent: c.parent,
  vertical: c.vertical,
}))

/* M4.6 example: Mumbai pickup → Hyderabad ×2 / Bengaluru / Bhopal. Hyderabad is a
   metro on the courier side, so Mumbai → Hyderabad resolves to Zone 3 (the
   precondition for the Acme Zone-2 override). */
const DEMO_PICKUP = '400001'
const DEMO_DROPS = ['500001', '500032', '560001', '462001']
const DEMO_METROS =
  METROS_SEED.indexOf('Hyderabad') >= 0 ? METROS_SEED.slice() : METROS_SEED.concat(['Hyderabad'])

function buildCourierRows() {
  const pu = pin(DEMO_PICKUP)
  return DEMO_DROPS.map((dp) => {
    const dr = pin(dp)
    const out = resolveZone(DEMO_PICKUP, dp, DEMO_METROS, SPECIALS_SEED)
    return {
      pickupPin: DEMO_PICKUP,
      pickupCity: pu.city,
      pickupState: pu.state,
      dropPin: dp,
      dropCity: dr.city,
      dropState: dr.state,
      zone: out.zone,
      rule: out.rule,
    }
  })
}

function dualRows(clientId) {
  const os = activeOverrides(clientId)
  return buildCourierRows().map((r) => {
    const co = resolveClientZone(r, os)
    return {
      r,
      clientZone: co ? co.zone : r.zone,
      overridden: !!co,
      matchField: co ? co.field : null,
    }
  })
}

export default function ZoneOverride() {
  const [clientId, setClientId] = useState((CODE_CLIENTS[0] || {}).id || '')
  const [rulesView, setRulesView] = useState(null) // null = auto, else 'cards' | 'table'
  const [previewFilter, setPreviewFilter] = useState('all')
  const [builderOpen, setBuilderOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deact, setDeact] = useState(null) // override pending deactivation
  const [tick, setTick] = useState(0) // force recompute after mutating OVERRIDES
  const [lastAudit, setLastAudit] = useState(null) // {clientId, by, at, reason?}
  const toast = useToast()

  const refresh = () => setTick((t) => t + 1)
  const client = CODE_CLIENTS.find((c) => c.id === clientId)

  // `tick` is an intentional dependency: it forces a recompute after the
  // module-level OVERRIDES array is mutated (add / deactivate).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ovrs = useMemo(() => (clientId ? activeOverrides(clientId) : []), [clientId, tick])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dual = useMemo(() => (clientId ? dualRows(clientId) : []), [clientId, tick])
  const ovrCount = dual.filter((d) => d.overridden).length

  const effectiveView = rulesView || (ovrs.length > 15 ? 'table' : 'cards')

  const saveOverride = (F) => {
    OVERRIDES.push({
      id: nextOverrideId(clientId),
      clientId,
      field: F.field,
      value: F.value,
      zone: +F.zone,
      reason: F.reason.trim(),
      from: F.from,
      to: F.to || null,
      agreement: F.agreement || null,
      status: 'Active',
      by: 'ashwani.kumar@prozo.com',
      at: TODAY.toISOString(),
    })
    setBuilderOpen(false)
    setLastAudit({clientId, by: 'ashwani.kumar@prozo.com', at: fmtDateTime(TODAY.toISOString())})
    refresh()
    toast(`Override saved for ${client.name}. Audit recorded.`, 'success')
  }

  const doDeactivate = (reason) => {
    if (!deact) return
    deact.status = 'Deactivated'
    deact.deact = {by: 'ashwani.kumar@prozo.com', at: '31 May 2026, 12:00 IST', reason}
    setDeact(null)
    setLastAudit({clientId, by: 'ashwani.kumar@prozo.com', at: fmtDateTime(TODAY.toISOString()), reason})
    refresh()
    toast('Override deactivated. Audit recorded.', 'success')
  }

  const exportPreview = () => {
    const head = [
      'pickup_pin',
      'pickup_city',
      'drop_pin',
      'drop_city',
      'courier_zone',
      'client_zone',
      'overridden',
      'match_field',
    ]
    const lines = [head.join(',')]
    dual.forEach((d) => {
      lines.push(
        [
          d.r.pickupPin,
          d.r.pickupCity,
          d.r.dropPin,
          d.r.dropCity,
          'Zone ' + d.r.zone,
          'Zone ' + d.clientZone,
          d.overridden ? 'yes' : 'no',
          d.overridden ? MATCH_LABEL[d.matchField] || d.matchField : '',
        ].join(',')
      )
    })
    download('dual-zone-' + clientId + '.csv', lines.join('\n'))
    toast('Exported dual-zone preview.', 'success')
  }

  return (
    <>
      <PageTitle>Zone Override</PageTitle>
      <style>{ZO_CSS}</style>

      <div className='page-head'>
        <div>
          <h1>Zone Override</h1>
          <p className='sub'>
            Account-code-level override layer above the courier-side base zones. When an override
            matches, the overridden zone wins for billing; the base courier zone is unchanged.
          </p>
        </div>
      </div>

      <div className='card card-pad' style={{marginBottom: 16, maxWidth: 560}}>
        <div className='field' style={{margin: 0}}>
          <label>
            Courier account code <span className='req'>*</span>
          </label>
          <Autocomplete
            options={CODE_CLIENTS}
            value={client || null}
            getOptionLabel={(o) => `${o.name} · ${o.parent}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(e, v) => {
              setClientId(v ? v.id : '')
              setRulesView(null)
              setPreviewFilter('all')
            }}
            disableClearable
            componentsProps={{paper: {className: 'supply-scope'}}}
            renderInput={(params) => (
              <TextField {...params} placeholder='Search courier account code…' size='small' />
            )}
          />
          <div className='help'>
            <InfoOutlinedIcon style={{width: 13, height: 13}} />
            The override applies only to this courier account code. Other codes keep the
            courier-side base zone.
          </div>
        </div>
      </div>

      {!clientId ? (
        <div className='empty'>
          <div className='ic'>
            <SettingsInputComponentOutlinedIcon style={{width: 30, height: 30}} />
          </div>
          <h3>Select a courier account code</h3>
          <p>An account code must be selected before override rows can be added.</p>
        </div>
      ) : (
        <>
          <KpiStrip
            cards={[
              {
                label: 'Active Overrides',
                value: ovrs.length,
                icon: <BoltOutlinedIcon />,
                sub: 'for ' + client.name,
              },
              {
                label: 'Rows Overridden',
                value: ovrCount,
                icon: <SyncIcon />,
                sub: 'in the preview below',
              },
              {label: 'Preview Rows', value: dual.length, icon: <TableChartOutlinedIcon />},
            ]}
          />

          <div className='card' style={{marginBottom: 16}}>
            <div className='card-hd'>
              <div>
                <h3>Override Rules — {client.name}</h3>
                {lastAudit && lastAudit.clientId === clientId ? (
                  <div style={{marginTop: 2}}>
                    <span className='muted' style={{fontSize: 11.5}}>
                      Last change: {lastAudit.by} · {lastAudit.at}
                      {lastAudit.reason ? ' · ' + lastAudit.reason : ''}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className='row gap8 wrap'>
                {ovrs.length ? (
                  <div className='zo-seg'>
                    <button
                      type='button'
                      className={effectiveView === 'cards' ? 'on' : ''}
                      onClick={() => setRulesView('cards')}
                    >
                      Cards
                    </button>
                    <button
                      type='button'
                      className={effectiveView === 'table' ? 'on' : ''}
                      onClick={() => setRulesView('table')}
                    >
                      Table
                    </button>
                  </div>
                ) : null}
                <button className='btn btn-sm btn-primary' onClick={() => setBuilderOpen(true)}>
                  <AddOutlinedIcon style={{width: 15, height: 15}} />
                  New Override
                </button>
                <button className='btn btn-sm' onClick={() => setUploadOpen(true)}>
                  <UploadOutlinedIcon style={{width: 15, height: 15}} />
                  Upload Override CSV
                </button>
              </div>
            </div>

            {!ovrs.length ? (
              <div className='card-pad'>
                <div className='empty' style={{padding: 24}}>
                  <div className='ic'>
                    <BoltOutlinedIcon style={{width: 26, height: 26}} />
                  </div>
                  <h3>No active overrides</h3>
                  <p>
                    Add a client override to force a specific billing zone for matching shipments.
                  </p>
                </div>
              </div>
            ) : effectiveView === 'cards' ? (
              <div className='zo-rules-grid card-pad'>
                {ovrs.map((o) => (
                  <RuleCard key={o.id} o={o} onDeactivate={() => setDeact(o)} />
                ))}
              </div>
            ) : (
              <RulesTable clientId={clientId} tick={tick} onDeactivate={setDeact} />
            )}
          </div>

          <DualZonePreview
            dual={dual}
            previewFilter={previewFilter}
            setPreviewFilter={setPreviewFilter}
            onExport={exportPreview}
          />
        </>
      )}

      {client ? (
        <Builder
          open={builderOpen}
          onClose={() => setBuilderOpen(false)}
          client={client}
          clientId={clientId}
          onSave={saveOverride}
          toast={toast}
        />
      ) : null}

      {client ? (
        <UploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          client={client}
          toast={toast}
        />
      ) : null}

      <ConfirmDialog
        open={!!deact}
        onClose={() => setDeact(null)}
        title='Deactivate this override?'
        danger
        needReason
        body='This stops applying the client zone for matching shipments. The courier zone is unaffected.'
        bullets={[
          'Matching rows revert to the courier-side zone',
          'The override remains in audit history',
        ]}
        okLabel='Deactivate'
        onOk={doDeactivate}
      />
    </>
  )
}

/* ───────── Rule card ───────── */
function RuleCard({o, onDeactivate}) {
  return (
    <div className='zo-rule'>
      <div className='zo-rule-top'>
        <div className='zo-when'>
          <span className='zo-if'>IF</span>{' '}
          <span className='tag'>{MATCH_LABEL[o.field] || o.field}</span>{' '}
          <span className='zo-eq'>=</span> <b>{o.value}</b>{' '}
          <span className='zo-arrow'>
            <ArrowForwardIcon style={{width: 15, height: 15}} />
          </span>{' '}
          <span className='zo-force'>force</span> <ZoneBadge zone={o.zone} />
        </div>
        <button className='iconbtn' title='Deactivate override' onClick={onDeactivate}>
          <PowerSettingsNewOutlinedIcon style={{width: 16, height: 16}} />
        </button>
      </div>
      <div className='zo-rule-meta'>
        <span>
          <CalendarTodayOutlinedIcon style={{width: 12, height: 12}} />
          {fmtDate(o.from)}
          {o.to ? ' → ' + fmtDate(o.to) : ' · open-ended'}
        </span>
        <span>
          <AgreementCell id={o.agreement} />
        </span>
      </div>
      <div className='zo-reason'>
        <span className='muted'>Reason:</span> {o.reason}
      </div>
    </div>
  )
}

/* ───────── Dense table fallback (>15 rules or user-toggled) ───────── */
function RulesTable({clientId, tick, onDeactivate}) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => activeOverrides(clientId), [clientId, tick])
  const columns = [
    {
      key: 'field',
      label: 'Match Field',
      render: (o) => <span className='tag'>{MATCH_LABEL[o.field] || o.field}</span>,
    },
    {key: 'value', label: 'Match Value', render: (o) => <b>{o.value}</b>},
    {
      key: 'zone',
      label: 'Forced Client Zone',
      align: 'center',
      render: (o) => <ZoneBadge zone={o.zone} />,
      exportValue: (o) => 'Zone ' + o.zone,
    },
    {
      key: 'reason',
      label: 'Reason',
      sortable: false,
      render: (o) => (
        <span title={o.reason}>
          {o.reason.length > 42 ? o.reason.slice(0, 42) + '…' : o.reason}
        </span>
      ),
    },
    {
      key: 'from',
      label: 'Effective',
      render: (o) => fmtDate(o.from) + (o.to ? ' → ' + fmtDate(o.to) : ' · open-ended'),
      value: (o) => o.from,
    },
    {
      key: 'agreement',
      label: 'Agreement',
      sortable: false,
      render: (o) => <AgreementCell id={o.agreement} />,
    },
    {
      key: 'audit',
      label: 'Audit',
      sortable: false,
      render: (o) => (
        <span className='muted' style={{fontSize: 11}}>
          {o.by}
          <br />
          {fmtDateTime(o.at)}
        </span>
      ),
    },
  ]
  return (
    <SupplyTable
      columns={columns}
      rows={rows}
      rowKey={(o) => o.id}
      searchKeys={['value', 'reason']}
      searchPlaceholder='Search overrides…'
      exportName={'zone-overrides-' + clientId}
      defaultSort={{key: 'from', dir: 'desc'}}
      pageSize={20}
      rowActions={(o) => [
        {
          label: 'Deactivate override',
          icon: <PowerSettingsNewOutlinedIcon fontSize='small' />,
          danger: true,
          onClick: () => onDeactivate(o),
        },
      ]}
    />
  )
}

/* ───────── Zone Override preview (base → override transform) ───────── */
function DualZonePreview({dual, previewFilter, setPreviewFilter, onExport}) {
  const shown = dual.filter((d) => previewFilter === 'all' || d.overridden)
  return (
    <div className='card' style={{marginTop: 16}}>
      <div className='card-hd'>
        <div>
          <h3>Zone Override Preview</h3>
          <div className='muted' style={{fontSize: 12, marginTop: 2}}>
            Base courier zone → overridden zone. When an override matches, the overridden zone wins
            for billing; the base courier zone is unchanged.
          </div>
        </div>
        <div className='row gap8 wrap'>
          <div className='zo-seg'>
            <button
              type='button'
              className={previewFilter === 'all' ? 'on' : ''}
              onClick={() => setPreviewFilter('all')}
            >
              All rows
            </button>
            <button
              type='button'
              className={previewFilter === 'overridden' ? 'on' : ''}
              onClick={() => setPreviewFilter('overridden')}
            >
              Overridden only
            </button>
          </div>
          <button className='btn btn-sm' onClick={onExport}>
            <DownloadOutlinedIcon style={{width: 15, height: 15}} />
            Export
          </button>
        </div>
      </div>
      <div className='tbl-wrap' style={{padding: '0 8px 8px'}}>
        <table className='zo-xtable'>
          <thead>
            <tr>
              <th>Pickup</th>
              <th>Drop</th>
              <th>Base → Override Zone</th>
              <th>Rule Fired</th>
            </tr>
          </thead>
          <tbody>
            {shown.length ? (
              shown.map((d) => (
                <tr className={cx('zo-xrow', d.overridden && 'ovr')} key={d.r.dropPin}>
                  <td>
                    <span className='mono'>{d.r.pickupPin}</span> {d.r.pickupCity}
                  </td>
                  <td>
                    <span className='mono'>{d.r.dropPin}</span> {d.r.dropCity}
                  </td>
                  <td>
                    {d.overridden ? (
                      <span className='zo-zcell'>
                        <ZoneBadge zone={d.r.zone} />
                        <span className='zo-arrow'>
                          <ArrowForwardIcon style={{width: 14, height: 14}} />
                        </span>
                        <ZoneBadge zone={d.clientZone} />
                        <BluePill
                          label={'override · ' + (MATCH_LABEL[d.matchField] || d.matchField)}
                        />
                      </span>
                    ) : (
                      <span className='zo-zcell'>
                        <ZoneBadge zone={d.r.zone} />
                        <span className='zo-courier-cap'>courier rule</span>
                      </span>
                    )}
                  </td>
                  <td>
                    {d.overridden ? (
                      <span className='tag'>
                        {'Zone override — ' + (MATCH_LABEL[d.matchField] || d.matchField)}
                      </span>
                    ) : (
                      <span className='muted' style={{fontSize: 12}}>
                        {d.r.rule}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className='muted' style={{padding: 16, textAlign: 'center'}}>
                  No overridden rows for this account code.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ───────── M4.7 rule builder (SideDrawer) ───────── */
function Builder({open, onClose, client, clientId, onSave, toast}) {
  const blank = {field: '', value: '', zone: '', reason: '', from: '', to: '', agreement: ''}
  const [F, setF] = useState(blank)
  const [err, setErr] = useState({})

  // reset form each time the drawer opens
  const [seenOpen, setSeenOpen] = useState(false)
  if (open && !seenOpen) {
    setF(blank)
    setErr({})
    setSeenOpen(true)
  }
  if (!open && seenOpen) setSeenOpen(false)

  const upd = (patch) => setF((p) => ({...p, ...patch}))
  const clearErr = (k) => setErr((e) => ({...e, [k]: false}))

  const agrOpts = DUMMY_AGREEMENTS.filter(
    (a) => a.vertical === 'Both' || a.vertical === client.vertical
  ).map((a) => ({
    value: a.id,
    label:
      (a.type === 'Commercial Addendum' ? 'Addendum #' + a.addendumNo : a.type) + ' · ' + a.parent,
  }))

  const valueControl = () => {
    if (F.field === 'destCity' || F.field === 'pickupCity')
      return (
        <ValueSelect
          options={uniqueCities()}
          value={F.value}
          invalid={err.value}
          help='City name resolved against the Pincode Master.'
          vmsg='Select a city.'
          onChange={(v) => {
            upd({value: v})
            clearErr('value')
          }}
        />
      )
    if (F.field === 'destState' || F.field === 'pickupState')
      return (
        <ValueSelect
          options={uniqueStates()}
          value={F.value}
          invalid={err.value}
          help='State name resolved against the Pincode Master.'
          vmsg='Select a state.'
          onChange={(v) => {
            upd({value: v})
            clearErr('value')
          }}
        />
      )
    if (F.field === 'pair')
      return (
        <ValueInput
          value={F.value}
          invalid={err.value}
          placeholder='400001-500001'
          help='Pickup-drop pincode pair, e.g. 400001-500001.'
          vmsg='Enter a pickup-drop pair.'
          onChange={(v) => {
            upd({value: v})
            clearErr('value')
          }}
        />
      )
    if (F.field)
      return (
        <ValueInput
          value={F.value}
          invalid={err.value}
          placeholder='e.g. 500001'
          help='Pincode that must exist in the Pincode Master.'
          vmsg='Enter a value to match.'
          onChange={(v) => {
            upd({value: v})
            clearErr('value')
          }}
        />
      )
    return (
      <div className='field'>
        <label>
          Match value <span className='req'>*</span>
        </label>
        <input className='input' disabled placeholder='Select a match field first' />
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Choose a match field to set its value.
        </div>
      </div>
    )
  }

  const onSaveClick = () => {
    const e = {}
    if (!F.field) e.field = true
    if (!String(F.value).trim()) e.value = true
    if (!F.zone) e.zone = true
    if (!String(F.reason).trim()) e.reason = true
    if (!F.from) e.from = true
    setErr(e)
    if (Object.keys(e).length) {
      toast('Complete the required fields. Reason is mandatory.', 'error')
      return
    }
    if (F.to && new Date(F.to) <= new Date(F.from)) {
      setErr({to: true})
      toast('Effective To must be after Effective From.', 'error')
      return
    }
    const clash = activeOverrides(clientId).some(
      (o) =>
        o.field === F.field &&
        String(o.value).toLowerCase() === String(F.value).toLowerCase() &&
        datesOverlap(o.from, o.to, F.from, F.to)
    )
    if (clash) {
      setErr({field: true, value: true})
      toast('An active override already exists for this client and match condition.', 'error')
      return
    }
    onSave(F)
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={'New Zone Override — ' + client.name}
      width={520}
      footer={
        <>
          <button className='btn btn-ghost' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={onSaveClick}>
            <CheckIcon style={{width: 16, height: 16}} />
            Save Override
          </button>
        </>
      }
    >
      <div className='warnbox info' style={{marginBottom: 14}}>
        <InfoOutlinedIcon />
        <div>
          Overrides change billing for courier account code <b>{client.name}</b> only. Reason is
          mandatory for audit.
        </div>
      </div>

      <FieldSelect
        label='Match field'
        value={F.field}
        invalid={err.field}
        options={MATCH_FIELDS}
        help='What this override matches against. Example: Destination city.'
        vmsg='Select a match field.'
        onChange={(v) => {
          upd({field: v, value: ''})
          clearErr('field')
        }}
      />

      {valueControl()}

      <FieldSelect
        label='Force client zone'
        value={F.zone}
        invalid={err.zone}
        options={[1, 2, 3, 4, 5].map((z) => ({
          value: z,
          label: 'Zone ' + z + ' · ' + ZONE_NAMES[z],
        }))}
        help='The zone this client is billed at when the rule matches.'
        vmsg='Force client zone cannot be blank.'
        onChange={(v) => {
          upd({zone: v})
          clearErr('zone')
        }}
      />

      <div className={cx('field', err.reason && 'invalid')}>
        <label>
          Reason <span className='req'>*</span>
        </label>
        <textarea
          className='input'
          rows={2}
          placeholder='Commercial reason for the override'
          value={F.reason}
          onChange={(e) => {
            upd({reason: e.target.value})
            clearErr('reason')
          }}
        />
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Required because this changes billing. Saved in audit.
        </div>
        <div className='vmsg'>A reason is mandatory for billing overrides.</div>
      </div>

      <div className='row gap12'>
        <div style={{flex: 1}}>
          <div className={cx('field', err.from && 'invalid')}>
            <label>
              Effective from <span className='req'>*</span>
            </label>
            <input
              className='input'
              type='date'
              value={F.from}
              onChange={(e) => {
                upd({from: e.target.value})
                clearErr('from')
              }}
            />
            <div className='help'>
              <InfoOutlinedIcon style={{width: 13, height: 13}} />
              Date from which this override applies.
            </div>
            <div className='vmsg'>Effective from is required.</div>
          </div>
        </div>
        <div style={{flex: 1}}>
          <div className={cx('field', err.to && 'invalid')}>
            <label>
              Effective to <span className='opt'>(optional)</span>
            </label>
            <input
              className='input'
              type='date'
              value={F.to}
              onChange={(e) => {
                upd({to: e.target.value})
                clearErr('to')
              }}
            />
            <div className='help'>
              <InfoOutlinedIcon style={{width: 13, height: 13}} />
              Blank = open-ended. Must be after Effective From.
            </div>
            <div className='vmsg'>Effective To must be after Effective From.</div>
          </div>
        </div>
      </div>

      <FieldSelect
        label='Linked agreement'
        value={F.agreement}
        optional
        options={agrOpts}
        help='Strongly recommended where the override is contractually agreed.'
        onChange={(v) => upd({agreement: v})}
      />
    </SideDrawer>
  )
}

/* ───────── Builder field primitives ───────── */
function FieldSelect({label, value, invalid, options, help, vmsg, optional, onChange}) {
  return (
    <div className={cx('field', invalid && 'invalid')}>
      <label>
        {label}{' '}
        {optional ? <span className='opt'>(optional)</span> : <span className='req'>*</span>}
      </label>
      <select className='input' value={value} onChange={(e) => onChange(e.target.value)}>
        <option value=''>Select…</option>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o
          const l = typeof o === 'object' ? o.label : o
          return (
            <option key={String(v)} value={v}>
              {l}
            </option>
          )
        })}
      </select>
      <div className='help'>
        <InfoOutlinedIcon style={{width: 13, height: 13}} />
        {help}
      </div>
      {vmsg ? <div className='vmsg'>{vmsg}</div> : null}
    </div>
  )
}

function ValueSelect({options, value, invalid, help, vmsg, onChange}) {
  return (
    <FieldSelect
      label='Match value'
      value={value}
      invalid={invalid}
      options={options}
      help={help}
      vmsg={vmsg}
      onChange={onChange}
    />
  )
}

function ValueInput({value, invalid, placeholder, help, vmsg, onChange}) {
  return (
    <div className={cx('field', invalid && 'invalid')}>
      <label>
        Match value <span className='req'>*</span>
      </label>
      <input
        className='input'
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className='help'>
        <InfoOutlinedIcon style={{width: 13, height: 13}} />
        {help}
      </div>
      <div className='vmsg'>{vmsg}</div>
    </div>
  )
}

/* ───────── CSV upload (SideDrawer + FileDrop, mockup PS.drawer) ───────── */
function UploadModal({open, onClose, client, toast}) {
  const [parsedName, setParsedName] = useState(null)
  const close = () => {
    setParsedName(null)
    onClose()
  }
  return (
    <SideDrawer
      open={open}
      onClose={close}
      title={'Upload Zone Override CSV — ' + client.name}
      width={480}
      footer={
        <button className='btn btn-ghost' onClick={close}>
          Close
        </button>
      }
    >
      <FileDrop
        accept='.csv'
        maxMB={10}
        title='Drop your zone override CSV here'
        hint='Columns: match_field, match_value, force_zone, reason · max 10 MB'
        sampleName='client-override-template.csv'
        sampleLabel='Download template'
        sampleContent={
          'match_field,match_value,force_zone,reason\ndestCity,Hyderabad,2,Negotiated FY27 commercials\n'
        }
        onFile={(file) => {
          setParsedName(file.name)
          toast(`Client override file parsed (${client.vertical}).`, 'success')
        }}
        onError={(m) => toast(m, 'error')}
      />
      {parsedName ? (
        <div className='warnbox ok' style={{marginTop: 10}}>
          <CheckCircleOutlinedIcon />
          <div>
            Parsed 1 override row from <b>{parsedName}</b>. Review before pushing to{' '}
            <span className='mono'>/v1/zone-mapping/client/{client.id}</span>.
          </div>
        </div>
      ) : null}
      <div className='warnbox info' style={{marginTop: 12}}>
        <InfoOutlinedIcon />
        <div>
          Endpoint on push: <span className='mono'>/v1/zone-mapping/courier/{client.id}</span>
        </div>
      </div>
    </SideDrawer>
  )
}
